import React, { Fragment, memo, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Text, Link, Advice, FieldGroup, Input } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import MobileSms from '../icons/MobileSms';
import SecurityLock from '../icons/SecurityLock';

import styles from './styles';
const useStyles = createUseStyles(styles, 'EnterCode');

const EnterCode = ({
    code,
    focus,
    wait,
    hiddenPhone,
    isFirtsTime,
    buttonRef,
    error,
    setCode,
    setFocus,
    onClickModifyPhone,
    generateCode,
}) => {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const [counter, setCounter] = useState(wait ? wait - Math.round(Date.now() / 1000) + 60 : 60);
    const inputRefs = useRef([]);

    useEffect(() => {
        inputRefs.current[focus] && inputRefs.current[focus].focus();
    }, [focus]);

    useEffect(() => {
        if (!wait) return;
        const interval = setInterval(() => {
            if (counter > 1) {
                setCounter(counter - 1);
            } else {
                setCounter(0);
                clearInterval(interval.current);
                generateCode();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [counter, generateCode, wait]);

    const onChange = useCallback(
        (values, value, index) => {
            setCode(values);
            if (value === '') {
                setFocus(index - 1);
            } else if (inputRefs.current[index + 1]) {
                setFocus(index + 1);
            } else {
                buttonRef.current.focus();
            }
        },
        [buttonRef, setCode, setFocus],
    );

    const onKeyDown = useCallback(
        (e) => {
            const index = parseInt(e.target.name, 10);
            if (e.keyCode === 8 && !e.target.value) {
                setFocus(index - 1);
                e.preventDefault();
            } else if (e.keyCode !== 8 && e.target.value) {
                setFocus(index + 1);
                e.preventDefault();
            }
        },
        [setFocus],
    );

    const onFocus = useCallback(
        (value, e) => {
            if (e.keyCode === 8 && !e.target.value) {
                setFocus(e.target.name);
            }
        },
        [setFocus],
    );

    const getRef = useCallback((index) => {
        return (ref) => {
            inputRefs.current[index] = ref;
        };
    }, []);

    const handleOnClickModifyPhone = useCallback(() => {
        onClickModifyPhone(true);
    }, [onClickModifyPhone]);

    const handleOnClickReSend = useCallback(() => {
        generateCode();
        setCode([]);
        setFocus(0);
    }, [generateCode, setCode, setFocus]);

    const commonProps = useMemo(
        () => ({
            onKeyDown,
            onFocus,
            maxLength: 1,
            type: 'integer',
            hideClear: true,
            classes: { input: classes.codeInputs },
            autoComplete: 'no',
        }),
        [classes.codeInputs, onFocus, onKeyDown],
    );

    const advice = useMemo(() => {
        if (error) {
            return { type: 'error', msg: error };
        }
        return { type: 'info', msg: t('cfm_label_2fa_enter_code_desc') };
    }, [error, t]);

    return (
        <div className={classes.root}>
            {!wait && (
                <Fragment>
                    {(isFirtsTime || error) && (
                        <Advice className={classes.advice} type={advice.type} showCollapse={false}>
                            {advice.msg}
                        </Advice>
                    )}
                    <div className={classes.intro}>
                        <MobileSms className={classes.introIcon} />
                        <div className={classes.introText}>
                            <Text>{t('cfm_label_2fa_enter_code_desc_2', [hiddenPhone])} </Text>
                            <Link onClick={handleOnClickModifyPhone}>
                                {t('cfm_action_2fa_modify_number')}
                            </Link>
                        </div>
                    </div>
                    <div className={classes.codeContainer}>
                        <FieldGroup
                            inputs={[Input, Input, Input, Input, Input, Input]}
                            inputProps={[
                                { ...commonProps, ref: getRef(0), name: '0' },
                                { ...commonProps, ref: getRef(1), name: '1' },
                                { ...commonProps, ref: getRef(2), name: '2' },
                                { ...commonProps, ref: getRef(3), name: '3' },
                                { ...commonProps, ref: getRef(4), name: '4' },
                                { ...commonProps, ref: getRef(5), name: '5' },
                            ]}
                            onChange={onChange}
                            value={code}
                            isFullWidth
                            error={!!error}
                        />
                    </div>
                    <div className={classes.resendContainer}>
                        <Text>{t('cfm_label_2fa_no_recived_sms')}</Text>
                        <Link onClick={handleOnClickReSend}>{t('cfm_action_2fa_resend_code')}</Link>
                    </div>
                </Fragment>
            )}
            {wait && (
                <div className={classes.wait}>
                    <SecurityLock className={classes.waitIcon} />
                    <div className={classes.waitTextContainer}>
                        <Text className={classes.waitText}>{t('cfm_label_2fa_wait')} </Text>
                        <Text className={classes.waitText} bold>
                            {t('cfm_label_seconds', [counter])}
                        </Text>
                        <Text className={classes.waitText}> {t('cfm_label_2fa_wait_2')}</Text>
                    </div>
                </div>
            )}
        </div>
    );
};

EnterCode.propTypes = {
    code: PropTypes.array,
    focus: PropTypes.number,
    wait: PropTypes.string,
    hiddenPhone: PropTypes.string,
    isFirtsTime: PropTypes.bool,
    buttonRef: PropTypes.object,
    error: PropTypes.string,
    setCode: PropTypes.func,
    setFocus: PropTypes.func,
    onClickModifyPhone: PropTypes.func,
    generateCode: PropTypes.func,
    finish2FA: PropTypes.func,
};

export default memo(EnterCode);
